import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import PropTypes from 'prop-types';
import './styles.css';
import { createUploadApiService } from './services/uploadApiService';
import { createManagementApiService } from './services/managementApiService';
import {
  resolveCaseStatusValue,
  resolveScanStatusValue
} from './helpers/statusNormalization';
import {
  getCaseUploadBlockedMessage,
  getPlaybackUnavailableMessage
} from './helpers/uploadDisplayMessages';
import { CaseFileGroup } from './components/CaseFileGroup';
import { UploadListSection } from './components/UploadListSection';
import { UploadProcessingOverlay } from './components/UploadProcessingOverlay';
import {
  isFailedStatus,
  isPlaybackAllowedRecordStatus,
  isSuccessfulLifecycleStatus,
  isSuccessfulStatus,
  isTerminalScanStatus,
  isUploadAllowedCaseStatus,
  isVirusStatus
} from './helpers/statusPredicates';
import { mapRemoteScanStatus } from './helpers/statusMapping';
import {
  getUploadRowPresentation,
  resolveScanStatusTitle
} from './helpers/uploadRowPresentation';
import { buildStatusSummaryDetail } from './helpers/statusSummary';
import {
  createPlaybackPopupWindow,
  openPlaybackPopupUrl,
  ensurePlaybackPath,
  resolvePlaybackUrl,
  showPlaybackPreparingState,
  writePlaybackPopupMessage
} from './helpers/playbackPopup';
import { createUploadRequests } from './helpers/uploadRequests';
import { useRemoteDeleteFlow } from './hooks/useRemoteDeleteFlow';
import { useRemoteReadModel } from './hooks/useRemoteReadModel';
import { useLocalQueueInteractions } from './hooks/useLocalQueueInteractions';
import { useUploadQueueDerivedModel, useUploadQueueState } from './hooks/useUploadQueueState';
import { useUploadWorkflow } from './hooks/useUploadWorkflow';
import { UiMessages, withReason } from './uiMessages';

function resolveManagementApiBaseUrl(config) {
  return String(config?.managementApiBaseUrl || '').replace(/\/$/, '');
}
import {
  DEFAULT_MAX_PARALLEL_PART_UPLOADS,
  DELETE_DIALOG_MIN_VISIBLE_MS,
  MAX_MEDIA_FILES,
  MEDIA_ACCEPT,
  MEDIA_EXTENSIONS,
  PROCESSING_MAX_TIME_SECONDS,
  MULTIPART_MAX_RETRY_COUNT,
  MULTIPART_RETRY_DELAY_MS,
  STATUS_EVENT_NAME,
  TRANSCRIPT_ACCEPT,
  TRANSCRIPT_EXTENSIONS
} from './uploadControlConfig';
import {
  formatSize,
  getExtension,
  getMediaCountLimitMessage,
  getMediaFileValidationMessage,
  getPageIntroMessage,
  getSupportedTypesMessage,
  getTranscriptFileValidationMessage
} from './helpers/uploadValidation';
import {
  getFirstRecordValue,
  getRecordFileType,
  getRecordLogicalName,
  getRecordMediaId,
  getRecordNameCandidates,
  getRecordObjectKey,
  getRecordOriginalFileName,
  getRecordSizeInBytes,
  getRecordStoredFileName,
  getRecordSubmittedAtUtc,
  getRecordTranscriptFileId,
  normalizeDisplayName
} from './helpers/caseFileRecordAccessors';
import { createCaseFileRecordNormalizationHelpers } from './helpers/caseFileRecordNormalization';
import { createCaseFileRecordStatusHelpers } from './helpers/caseFileRecordStatus';
import { createCaseFileCollectionHelpers } from './helpers/caseFileCollections';
import { createLocalRemoteReconciliationHelpers } from './helpers/localRemoteReconciliation';
import { createRemoteProcessingTimeoutHelpers } from './helpers/remoteProcessingTimeout';
import {
  createLocalId,
  createMediaItem,
  waitForMilliseconds,
  waitForNextPaint
} from './helpers/uploadLocalState';
import {
  createUploadSelectionHelpers
} from './helpers/uploadSelection';

const VIRUS_BLOCK_MESSAGE = UiMessages.virusBlocked;
const GENERIC_UPLOAD_FAILURE_MESSAGE = "We're having trouble uploading your files right now. Please wait a moment and try again";
const DESCRIPTION_CONTROL_CHARACTER_PATTERN = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;
const DESCRIPTION_ANGLE_BRACKET_PATTERN = /[<>]/g;

function sanitizeEditableDescription(value) {
  return String(value ?? '')
    .replace(DESCRIPTION_CONTROL_CHARACTER_PATTERN, '')
    .replace(DESCRIPTION_ANGLE_BRACKET_PATTERN, '');
}

function normalizeEditableDescription(value) {
  return sanitizeEditableDescription(value).trim();
}

function isTranscriptDescriptionRecord(record, getRecordFileType) {
  return String(getRecordFileType(record) || '').trim().toLowerCase() === 'transcript';
}

function isMediaDescriptionRecord(record, getRecordFileType) {
  return String(getRecordFileType(record) || '').trim().toLowerCase() === 'media';
}

function patchCaseFileDescriptionRecords(records, { mediaId, isTranscript, description, getRecordMediaId, getRecordFileType }) {
  const normalizedMediaId = String(mediaId || '').trim().toLowerCase();

  return (records ?? []).map((record) => {
    const recordMediaId = String(getRecordMediaId(record) || '').trim().toLowerCase();
    if (!normalizedMediaId || recordMediaId !== normalizedMediaId) {
      return record;
    }

    const matchesRecordType = isTranscript
      ? isTranscriptDescriptionRecord(record, getRecordFileType)
      : isMediaDescriptionRecord(record, getRecordFileType);

    if (!matchesRecordType) {
      return record;
    }

    return {
      ...record,
      description,
      Description: description
    };
  });
}

function resolveMaxParallelPartUploads(config) {
  const rawValue = Number(config?.maxParallelPartUploads);
  if (!Number.isFinite(rawValue) || rawValue <= 0) {
    return DEFAULT_MAX_PARALLEL_PART_UPLOADS;
  }

  return Math.max(1, Math.floor(rawValue));
}

function isUploadWorkflowComplete(item) {
  if (!item) {
    return false;
  }

  if (item.uploadComplete === true) {
    return true;
  }

  const mediaDone = item.mediaUploadStatus === 'completed';
  const transcriptDone = !item.transcriptEnabled || item.transcriptUploadStatus === 'completed';
  return mediaDone && transcriptDone;
}

function isLocalUploadFailed(item) {
  if (!item) {
    return false;
  }

  const mediaScanStatus = resolveScanStatusValue(item.scanStatus);
  const transcriptScanStatus = resolveScanStatusValue(item.transcriptScanStatus);

  return item.mediaUploadStatus === 'error'
    || item.transcriptUploadStatus === 'error'
    || isFailedStatus(mediaScanStatus)
    || isFailedStatus(transcriptScanStatus)
    || mediaScanStatus === 'failed'
    || transcriptScanStatus === 'failed';
}

function isLocalUploadSuccessful(item) {
  if (!item) {
    return false;
  }

  const mediaScanStatus = resolveScanStatusValue(item.scanStatus);
  const transcriptScanStatus = resolveScanStatusValue(item.transcriptScanStatus);

  return item.mediaUploadStatus === 'completed'
    && (!item.transcriptEnabled || item.transcriptUploadStatus === 'completed')
    && isSuccessfulStatus(mediaScanStatus)
    && (!item.transcriptEnabled || isSuccessfulStatus(transcriptScanStatus));
}

function isLocalUploadInProgress(item) {
  if (!item || isLocalUploadFailed(item) || isLocalUploadSuccessful(item)) {
    return false;
  }

  if (item.mediaUploadStatus === 'uploading' || item.transcriptUploadStatus === 'uploading') {
    return true;
  }

  const mediaScanStatus = resolveScanStatusValue(item.scanStatus);
  const transcriptScanStatus = resolveScanStatusValue(item.transcriptScanStatus);

  if (item.mediaUploadStatus === 'completed' && !isTerminalScanStatus(mediaScanStatus)) {
    return true;
  }

  if (item.transcriptEnabled
    && item.transcriptUploadStatus === 'completed'
    && !isTerminalScanStatus(transcriptScanStatus)) {
    return true;
  }

  return false;
}

function hasPendingScanWork(items) {
  return items.some((item) => isLocalUploadInProgress(item));
}

function hasPendingLocalScanStatus(item) {
  return isLocalUploadInProgress(item);
}

function isPendingRemoteScanRecord(record) {
  if (!record) {
    return false;
  }

  if (isVirusDetectedRecord(record)) {
    return false;
  }

  const scanStatus = getRecordScanStatus(record);
  const rawStatus = resolveScanStatusValue(scanStatus);

  // Some historical records do not expose any scan status at all.
  // Do not keep polling forever for those rows.
  if (!rawStatus) {
    return false;
  }

  const mappedStatus = mapRemoteScanStatus(scanStatus);
  return !isTerminalScanStatus(mappedStatus);
}

function hasPendingRemoteScanWork(records) {
  return buildCaseFileItems(records ?? []).some((item) => {
    const mediaPending = isPendingRemoteScanRecord(item?.mediaRecord);
    const transcriptRecord = getEffectiveTranscriptRecord(item?.mediaRecord, item?.transcriptRecord);
    const transcriptPending = isPendingRemoteScanRecord(transcriptRecord);

    return mediaPending || transcriptPending;
  });
}

function hasPendingAnyScanWork(localItems, remoteRecords) {
  return hasPendingScanWork(localItems ?? []) || hasPendingRemoteScanWork(remoteRecords ?? []);
}

// Keep these factory bindings together: status helpers depend on
// `getEffectiveTranscriptRecord(...)`, while normalization helpers need lazy
// closures back to `getRecordScanStatus(...)` / `getRecordLifecycleStatus(...)`.
const {
  buildTranscriptStatusOverlay,
  mergeTranscriptRecordCandidates,
  getEmbeddedTranscriptRecord,
  getEffectiveTranscriptRecord,
  unwrapCaseFileRecord,
  mapStatusSummaryFilesToCaseFileRecords,
  normalizeCaseFileRecords,
  hasMeaningfulTranscriptRecord
} = createCaseFileRecordNormalizationHelpers({
  chooseBetterCaseFileRecord: (...args) => chooseBetterCaseFileRecord(...args),
  getRecordScanStatus: (...args) => getRecordScanStatus(...args),
  getRecordLifecycleStatus: (...args) => getRecordLifecycleStatus(...args)
});

const {
  isVirusDetectedRecord,
  isVirusDetectedMediaRecord,
  isTranscriptVirusDetected,
  getRecordScanStatus,
  getRecordLifecycleStatus,
  getRecordDisplayStatus,
  isScanningLifecycleStatus,
  hasExplicitCleanScanStatus,
  hasExplicitVirusScanStatus,
  resolveToneFromLocalStatus,
  resolveHigherPriorityTone,
  resolveRemoteMediaTone,
  resolveRemoteTranscriptTone,
  resolveCaseFileMediaTone,
  resolveCaseFileMediaTitle,
  resolveCaseFileTranscriptTone,
  resolveCaseFileTranscriptTitle,
  isMediaCaseRecord,
  isTranscriptCaseRecord,
  isCleanCaseFileRecord,
  isCleanCaseFileItem,
  hasVirusDetectedInCaseFiles
} = createCaseFileRecordStatusHelpers({
  getEffectiveTranscriptRecord,
  resolveScanStatusTitle,
  isUploadWorkflowComplete,
  MEDIA_EXTENSIONS,
  TRANSCRIPT_EXTENSIONS
});

const {
  getCaseFileLogicalKeys,
  getCaseFileLogicalKey,
  getScanPriority,
  getRecordCompletenessScore,
  chooseBetterCaseFileRecord,
  dedupeCaseFileRecordsExact,
  dedupeCaseFileRecords,
  getCaseFileTimestamp,
  compareCaseFileItems,
  buildCaseFileItems,
  buildCaseFileKey
} = createCaseFileCollectionHelpers({
  normalizeDisplayName,
  getFirstRecordValue,
  getRecordFileType,
  getRecordMediaId,
  getRecordNameCandidates,
  getRecordObjectKey,
  getRecordOriginalFileName,
  getRecordLogicalName,
  getRecordSizeInBytes,
  getRecordTranscriptFileId,
  getRecordScanStatus: (...args) => getRecordScanStatus(...args),
  getRecordLifecycleStatus: (...args) => getRecordLifecycleStatus(...args),
  isTranscriptCaseRecord: (...args) => isTranscriptCaseRecord(...args),
  isMediaCaseRecord: (...args) => isMediaCaseRecord(...args),
  isVirusDetectedRecord: (...args) => isVirusDetectedRecord(...args),
  isVirusStatus,
  isSuccessfulStatus,
  isFailedStatus,
  isSuccessfulLifecycleStatus,
  isScanningLifecycleStatus: (...args) => isScanningLifecycleStatus(...args),
  getEffectiveTranscriptRecord: (...args) => getEffectiveTranscriptRecord(...args)
});

const {
  localUploadItemMatchesRemoteItem,
  annotateRemoteCaseFileItems,
  mergeRemoteStatuses
} = createLocalRemoteReconciliationHelpers({
  normalizeDisplayName,
  getFirstRecordValue,
  getRecordMediaId,
  getRecordStoredFileName,
  getRecordTranscriptFileId,
  getEffectiveTranscriptRecord: (...args) => getEffectiveTranscriptRecord(...args),
  buildCaseFileItems,
  dedupeCaseFileRecords,
  chooseBetterCaseFileRecord,
  isMediaCaseRecord: (...args) => isMediaCaseRecord(...args),
  isTranscriptCaseRecord: (...args) => isTranscriptCaseRecord(...args),
  mapRemoteScanStatus,
  getRecordScanStatus: (...args) => getRecordScanStatus(...args),
  isVirusStatus,
  isSuccessfulStatus
});

const {
  buildProcessingDeadlineBatchItem,
  collectDeadlineMatchedRemoteScanCandidates,
  collectSubmittedAtTimedOutRemoteScanCandidates,
  buildTimedOutRemoteScanCandidateRequestKey
} = createRemoteProcessingTimeoutHelpers({
  normalizeDisplayName,
  getFirstRecordValue,
  getRecordMediaId,
  getRecordTranscriptFileId,
  getRecordStoredFileName,
  getRecordOriginalFileName,
  getRecordSubmittedAtUtc,
  getRecordScanStatus,
  isTranscriptCaseRecord,
  isPendingRemoteScanRecord,
  dedupeCaseFileRecords,
  buildCaseFileKey,
  isTerminalScanStatus,
  defaultMaxProcessingSeconds: PROCESSING_MAX_TIME_SECONDS
});

function removeCaseFileItemFromRecords(records, itemToRemove) {
  if (!itemToRemove) {
    return records ?? [];
  }

  const transcriptRecordToRemove = getEffectiveTranscriptRecord(itemToRemove?.mediaRecord, itemToRemove?.transcriptRecord ?? null);
  const mediaIdToRemove = normalizeDisplayName(getRecordMediaId(itemToRemove?.mediaRecord));
  const transcriptIdToRemove = normalizeDisplayName(getRecordTranscriptFileId(transcriptRecordToRemove));
  const mediaNamesToRemove = new Set(getRecordNameCandidates(itemToRemove?.mediaRecord));
  const transcriptNamesToRemove = new Set(getRecordNameCandidates(transcriptRecordToRemove));

  return (records ?? []).filter((record) => {
    const recordMediaId = normalizeDisplayName(getRecordMediaId(record));
    const recordTranscriptId = normalizeDisplayName(getRecordTranscriptFileId(record));
    const recordNames = getRecordNameCandidates(record);

    if (mediaIdToRemove && recordMediaId && recordMediaId === mediaIdToRemove) {
      return false;
    }

    if (transcriptIdToRemove && recordTranscriptId && recordTranscriptId === transcriptIdToRemove) {
      return false;
    }

    if (recordNames.some((name) => mediaNamesToRemove.has(name) || transcriptNamesToRemove.has(name))) {
      return false;
    }

    return true;
  });
}

function clearResolvedInlineWarningIfNeeded(message, localItems, remoteRecords) {
  if (!message || !isStaleInlineValidationMessage(message)) {
    return message;
  }

  if (message === VIRUS_BLOCK_MESSAGE) {
    const hasLocalVirus = (localItems ?? []).some((item) => shouldCountLocalUploadItem(item, remoteRecords) && (isVirusStatus(item.scanStatus) || isVirusStatus(item.transcriptScanStatus)));
    return hasVirusDetectedInCaseFiles(remoteRecords, buildCaseFileItems) || hasLocalVirus ? message : '';
  }

  const currentLimitMessage = getUploadLimitValidationMessage(localItems, remoteRecords);
  return currentLimitMessage === message ? message : '';
}

function getLocalUploadNameCandidates(item) {
  const names = [
    item?.mediaStoredFileName,
    item?.mediaFile?.name,
    item?.transcriptStoredFileName,
    item?.transcriptFile?.name
  ]
    .map((value) => normalizeDisplayName(value))
    .filter(Boolean);

  const expanded = new Set(names);
  names.forEach((name) => {
    expanded.add(name.replace(/^media-/, ''));
    expanded.add(name.replace(/^transcript-/, ''));
    expanded.add(name.replace(/^media[-_]/, ''));
    expanded.add(name.replace(/^transcript[-_]/, ''));
  });

  return Array.from(expanded.values()).filter(Boolean);
}

function shouldCountLocalUploadItem(item, remoteRecords = []) {
  if (!item?.mediaFile) {
    return false;
  }

  if (isLocalUploadFailed(item)) {
    return true;
  }

  if (isLocalUploadSuccessful(item)) {
    return false;
  }

  // Re-upload is allowed for selected files even if the same file already exists
  // remotely. The selected row stays the source of truth for the next upload run.
  if (isUploadWorkflowComplete(item)) {
    return false;
  }

  return true;
}

function shouldShowUploadQueueItem(item) {
    if (!item?.mediaFile) {
    return false;
  }

  if (item.mediaUploadStatus === 'uploading' || item.transcriptUploadStatus === 'uploading') {
    return true;
  }

  if (isLocalUploadFailed(item)) {
    return true;
  }

  return !isUploadWorkflowComplete(item);
}

function buildTimedOutCandidateRequestKey(candidate) {
  return buildTimedOutRemoteScanCandidateRequestKey(candidate);
}

function isStaleInlineValidationMessage(message) {
  return message === getMediaCountLimitMessage()
    || message === VIRUS_BLOCK_MESSAGE;
}

function resolveUploadZone(rawUploadZone) {
  return String(rawUploadZone || '').trim().toLowerCase() === 'internal'
    ? 'internal'
    : 'external';
}

function normalizeUploadWebControlConfig(config) {
  const normalizedConfig = config && typeof config === 'object'
    ? { ...config }
    : {};

  normalizedConfig.uploadZone = resolveUploadZone(normalizedConfig.uploadZone);
  normalizedConfig.isInternalZoneUpload = normalizedConfig.uploadZone === 'internal';
  return normalizedConfig;
}

function loadConfig() {
  const configElement = document.getElementById('upload-web-control-config');
  if (configElement?.textContent) {
    try {
      return normalizeUploadWebControlConfig(JSON.parse(configElement.textContent));
    } catch {
      return normalizeUploadWebControlConfig({});
    }
  }

  return normalizeUploadWebControlConfig(globalThis.__UPLOAD_WEB_CONTROL_CONFIG__ ?? {});
}

function isInternalUserRole(config) {
  return String(config?.role || '').trim().toLowerCase() === 'internaluser';
}

async function fetchApplicationFileStatusSummary(managementApiService, config) {
  if (!managementApiService?.baseUrl || !config?.caseId) {
    return null;
  }

  let response;
  if (isInternalUserRole(config)) {
    response = await managementApiService.getInternalCaseFiles(config.caseId);
  } else {
    if (!config?.clientId || !config?.applicationNumber) {
      return null;
    }

    response = await managementApiService.getApplicationFileStatusSummary({
      clientId: config.clientId,
      applicationNumber: config.applicationNumber,
      caseId: config.caseId
    });
  }

  if (!response.ok) {
    throw new Error(`status summary request failed (${response.status})`);
  }

  const payload = await response.json().catch(() => null);
  return payload?.data ?? null;
}

function UploadWidget({ config }) { // NOSONAR
  const debugLog = useCallback(() => {
    // Debug logging intentionally disabled for stable runtime behavior.
  }, []);
  const uploadApiService = useMemo(
    () => createUploadApiService(config),
    [config.uploadS3ApiBaseUrl, config.clientId, config.callbackToken, config.xCallbackToken, config.clientHostname]
  );
  const managementApiService = useMemo(
    () => createManagementApiService(config),
    [config.managementApiBaseUrl, config.clientId, config.callbackToken, config.xCallbackToken, config.clientHostname]
  );
  const uploadRequests = useMemo(() => createUploadRequests({
    config,
    uploadApiService,
    managementApiService,
    debugLog,
    getExtension,
    resolveMaxParallelPartUploads,
    waitForMilliseconds,
    multipartMaxRetryCount: MULTIPART_MAX_RETRY_COUNT,
    multipartRetryDelayMs: MULTIPART_RETRY_DELAY_MS,
    uiMessages: UiMessages
  }), [config.caseId, config.applicationNumber, config.clientId, config.userId, config.uploadS3ApiBaseUrl, uploadApiService, managementApiService, debugLog]);
  const isReadOnly = (config.controlMode ?? 'edit') === 'display';
  const selectionHelpers = useMemo(() => createUploadSelectionHelpers({
    maxMediaFiles: MAX_MEDIA_FILES,
    createLocalId,
    createMediaItem,
    getExtension,
    getMediaCountLimitMessage,
    getMediaFileValidationMessage,
    buildCaseFileItems,
    shouldCountLocalUploadItem,
    normalizeDisplayName,
    getEffectiveTranscriptRecord,
    getRecordMediaId,
    getRecordSizeInBytes
  }), []);
  const {
    createSelectionFeedbackItem,
    createSelectionSummaryDialog,
    classifyMediaFilesForSelection,
    getUploadLimitTotals,
    getUploadLimitValidationMessage
  } = selectionHelpers;

  const [inlineError, setInlineError] = useState('');
  const acknowledged = true;
  const {
    mediaItems,
    setMediaItems,
    mediaItemsRef,
    completionDialog,
    setCompletionDialog,
    selectionFeedback,
    setSelectionFeedback,
    pendingDeleteItemId,
    setPendingDeleteItemId,
    pendingTranscriptDeleteItemId,
    setPendingTranscriptDeleteItemId,
    localDeleteInProgress,
    setLocalDeleteInProgress,
    transcriptDeleteInProgress,
    setTranscriptDeleteInProgress,
    isUiLocked,
    setIsUiLocked,
    mediaInputRef,
    transcriptInputRefs,
    setTranscriptInputRef,
    updateMediaItem,
    updateItemStatus
  } = useUploadQueueState();
  const caseFilePollingTimerRef = useRef(null);
  const caseFilePollingInFlightRef = useRef(false);
  const caseFilePollingStoppedRef = useRef(false);
  const caseFilePollingActiveRef = useRef(false);
  const initialCaseFilesLoadKeyRef = useRef('');
  const processingTimeoutRequestKeysRef = useRef(new Set());
  const queueCaseFilePollingRef = useRef(null);
  const remoteTranscriptInputRefs = useRef({});
  const [remoteTranscriptActionMediaId, setRemoteTranscriptActionMediaId] = useState('');

  const {
    remoteStatusSummary,
    remoteStatusSummaryRef,
    refreshRemoteStatusSummary, // NOSONAR
    resetRemoteStatusSummary, // NOSONAR
    caseFiles,
    caseFilesLoading,
    caseFilesError,
    caseFilesRef,
    setCaseFiles,
    setCaseFilesError,
    fetchCaseFiles,
    loadCaseFiles
  } = useRemoteReadModel({
    config,
    debugLog,
    fetchApplicationFileStatusSummary,
    managementApiService,
    normalizeCaseFileRecords,
    mapStatusSummaryFilesToCaseFileRecords,
    normalizeDisplayName,
    getFirstRecordValue,
    getRecordMediaId,
    getRecordTranscriptFileId,
    getRecordFileType,
    getRecordScanStatus,
    getRecordLifecycleStatus,
    dedupeCaseFileRecordsExact,
    collectSubmittedAtTimedOutRemoteScanCandidates,
    processTimedOutRemoteScanCandidates,
    mergeRemoteStatuses,
    setMediaItems,
    mediaItemsRef,
    setInlineError,
    clearResolvedInlineWarningIfNeeded
  });

  const patchRemoteDescriptionState = useCallback(({ mediaId, isTranscript, description }) => {
    const nextCaseFiles = patchCaseFileDescriptionRecords(caseFilesRef.current, {
      mediaId,
      isTranscript,
      description,
      getRecordMediaId,
      getRecordFileType
    });

    caseFilesRef.current = nextCaseFiles;
    setCaseFiles(nextCaseFiles);
  }, [caseFilesRef, getRecordFileType, getRecordMediaId, setCaseFiles]);

  const caseStatus = resolveCaseStatusValue(getFirstRecordValue(remoteStatusSummary, ['caseStatus', 'CaseStatus']));
  const hasResolvedCaseStatus = Boolean(caseStatus);
  const isCaseBlockedByStatus = hasResolvedCaseStatus && !isUploadAllowedCaseStatus(caseStatus);
  const isUploadBlockedByCaseStatus = !isReadOnly && isCaseBlockedByStatus;
  const canRenderUploadControls = !isReadOnly && !isUploadBlockedByCaseStatus;
  const caseUploadBlockedMessage = isCaseBlockedByStatus ? getCaseUploadBlockedMessage(config.caseTitle, caseStatus) : '';

  const {
    interactionLocked,
    totalSelectedBytes,
    totalMediaCount,
    validationMessage,
    hasVirusDetected,
    hasPendingScanWorkNow,
    pendingUploadItems,
    uploadQueueItems,
    localUploadingCount,
    localSelectedNotUploadedCount,
    visibleUploadItems,
    failedUploadQueuePanelStyle,
    completionSummary
  } = useUploadQueueDerivedModel({
    isReadOnly,
    isUiLocked,
    mediaItems,
    caseFiles,
    inlineError,
    setInlineError,
    caseUploadBlockedMessage,
    selectionFeedback,
    acknowledged,
    getUploadLimitTotals,
    getUploadLimitValidationMessage,
    shouldCountLocalUploadItem,
    hasPendingLocalScanStatus,
    shouldShowUploadQueueItem,
    isLocalUploadFailed,
    isLocalUploadSuccessful,
    isLocalUploadInProgress,
    hasPendingAnyScanWork,
    hasVirusDetectedInCaseFiles: (records) => hasVirusDetectedInCaseFiles(records, buildCaseFileItems),
    isVirusStatus,
    virusBlockMessage: VIRUS_BLOCK_MESSAGE,
    isStaleInlineValidationMessage,
    getMediaFileValidationMessage,
    getTranscriptFileValidationMessage
  });

  const showVirusBanner = !isReadOnly && hasVirusDetected;
  const visibleCaseFileItems = useMemo(() => (isReadOnly ? buildCaseFileItems(caseFiles).filter((item) => isCleanCaseFileItem(item)) : buildCaseFileItems(caseFiles)), [caseFiles, isReadOnly]);

  useEffect(() => {
    debugLog('render-state', {
      mediaItemsCount: mediaItems.length,
      pendingUploadItemsCount: pendingUploadItems.length,
      uploadQueueItemsCount: uploadQueueItems.length,
      visibleUploadItemsCount: visibleUploadItems.length,
      isUiLocked,
      caseFilesError,
      validationMessage,
      acknowledged,
      interactionLocked,
      totalSelectedBytes,
      totalMediaCount
    });
  }, [acknowledged, caseFilesError, debugLog, interactionLocked, isUiLocked, mediaItems.length, pendingUploadItems.length, totalMediaCount, totalSelectedBytes, uploadQueueItems.length, validationMessage, visibleUploadItems.length]);

  const caseFilesLoadKey = useMemo(() => [
    managementApiService?.baseUrl || '',
    config.caseId || '',
    config.clientId || '',
    config.applicationNumber || '',
    config.role || ''
  ].join('|'), [
    managementApiService?.baseUrl,
    config.caseId,
    config.clientId,
    config.applicationNumber,
    config.role
  ]);

  useEffect(() => {
    if (!managementApiService?.baseUrl || !config.caseId) {
      debugLog('case-files-initial-load-skipped', {
        reason: 'missing-base-url-or-case-id',
        hasBaseUrl: Boolean(managementApiService?.baseUrl),
        caseId: config.caseId || ''
      });
      return;
    }

    if (initialCaseFilesLoadKeyRef.current === caseFilesLoadKey) {
      debugLog('case-files-initial-load-skipped', {
        reason: 'same-load-key',
        caseFilesLoadKey
      });
      return;
    }

    debugLog('case-files-initial-load-triggered', {
      caseFilesLoadKey,
      previousLoadKey: initialCaseFilesLoadKeyRef.current,
      baseUrl: managementApiService?.baseUrl,
      caseId: config.caseId,
      clientId: config.clientId,
      applicationNumber: config.applicationNumber,
      role: config.role
    });

    initialCaseFilesLoadKeyRef.current = caseFilesLoadKey;
    loadCaseFiles(true);
  }, [caseFilesLoadKey, config.caseId, config.clientId, config.applicationNumber, config.role, debugLog, loadCaseFiles, managementApiService?.baseUrl]);

  useEffect(() => {
    const detail = buildStatusSummaryDetail({
      config,
      acknowledged,
      validationMessage,
      hasVirusDetected,
      completionSummary,
      mediaItems,
      totalSelectedBytes,
      localUploadingCount,
      localSelectedNotUploadedCount,
      pendingUploadCount: pendingUploadItems.length,
      remoteStatusSummary: remoteStatusSummaryRef.current
    });

    globalThis.__UPLOAD_WEB_CONTROL_STATUS__ = detail;
    globalThis.dispatchEvent(new CustomEvent(STATUS_EVENT_NAME, { detail }));
    document.dispatchEvent(new CustomEvent(STATUS_EVENT_NAME, { detail }));
  }, [acknowledged, completionSummary, config, hasVirusDetected, localSelectedNotUploadedCount, localUploadingCount, mediaItems, pendingUploadItems.length, totalSelectedBytes, validationMessage]);

  const stopCaseFilePolling = useCallback(() => {
    caseFilePollingStoppedRef.current = true;
    caseFilePollingActiveRef.current = false;

    if (caseFilePollingTimerRef.current !== null) {
      globalThis.clearTimeout(caseFilePollingTimerRef.current);
      caseFilePollingTimerRef.current = null;
    }
  }, []);

  const openPlayerPopup = useCallback(async (item) => {
    if (isCaseBlockedByStatus) {
      setInlineError(caseUploadBlockedMessage || UiMessages.playbackUnavailable);
      return;
    }

    const mediaRecord = item?.mediaRecord ?? null;
    const mediaId = getRecordMediaId(mediaRecord);
    const caseId = config.caseId || '';
    const recordStatus = getFirstRecordValue(mediaRecord, ['recordStatus', 'RecordStatus']);

    if (!isPlaybackAllowedRecordStatus(recordStatus)) {
      const mediaFileName = getFirstRecordValue(mediaRecord, ['originalFileName', 'OriginalFileName', 'fileName', 'FileName', 'storedFileName', 'StoredFileName']) || mediaId || 'Media file';
      setInlineError(getPlaybackUnavailableMessage(mediaFileName, recordStatus));
      return;
    }

    if (!mediaId || !caseId) {
      setInlineError(UiMessages.playbackMissingIds);
      return;
    }

    const popup = createPlaybackPopupWindow();
    if (!popup) {
      setInlineError(UiMessages.popupBlocked);
      return;
    }

    const tryOpenPlayback = async () => {
      try {
        setInlineError('');
        showPlaybackPreparingState(popup, UiMessages);
        const playbackUrl = await resolvePlaybackUrl({
          caseId,
          mediaId,
          isInternalZoneRequest: config.isInternalZoneUpload === true,
          managementApiService,
          getFirstRecordValue,
          uiMessages: UiMessages
        });
        await openPlaybackPopupUrl({
          popup,
          playbackUrl: ensurePlaybackPath(playbackUrl)
        });
      } catch (error) {
        const errorMessage = error?.message || UiMessages.playbackOpenFailed;
        writePlaybackPopupMessage(popup, errorMessage, UiMessages.playbackPreparingTitle);
      }
    };

    tryOpenPlayback();
  }, [caseUploadBlockedMessage, config.caseId, isCaseBlockedByStatus, managementApiService]);

  function showUploadBlockedDialog(message) {
    setInlineError(message);
    setCompletionDialog({
      tone: 'error',
      title: 'Upload blocked',
      message
    });
  }

  const {
    handleMediaFileChange,
    handleDrop,
    handleTranscriptToggle,
    handleTranscriptFileChange,
    handleRemoveMediaFile, // NOSONAR
    handleRemoveTranscriptFile,
    handleDescriptionChange,
    handleTranscriptDescriptionChange,
    confirmRemoveMediaFile,
    confirmRemoveTranscriptFile
  } = useLocalQueueInteractions({
    interactionLocked,
    isUploadBlockedByCaseStatus,
    mediaItemsRef,
    caseFilesRef,
    mediaInputRef,
    transcriptInputRefs,
    setMediaItems,
    updateMediaItem,
    setInlineError,
    setCompletionDialog,
    setSelectionFeedback,
    getUploadLimitValidationMessage,
    classifyMediaFilesForSelection,
    createSelectionFeedbackItem,
    createSelectionSummaryDialog,
    createMediaItem,
    debugLog,
    getTranscriptFileValidationMessage,
    showUploadBlockedDialog,
    pendingDeleteItemId,
    setPendingDeleteItemId,
    pendingTranscriptDeleteItemId,
    setPendingTranscriptDeleteItemId,
    localDeleteInProgress,
    setLocalDeleteInProgress,
    transcriptDeleteInProgress,
    setTranscriptDeleteInProgress,
    loadCaseFiles
  });

  const setRemoteTranscriptInputRef = useCallback((mediaId, element) => {
    const normalizedMediaId = String(mediaId || '').trim();
    if (!normalizedMediaId) {
      return;
    }

    if (element) {
      remoteTranscriptInputRefs.current[normalizedMediaId] = element;
      return;
    }

    delete remoteTranscriptInputRefs.current[normalizedMediaId];
  }, []);

  const handleBrowseAddTranscript = useCallback((item) => {
    const mediaId = getRecordMediaId(item?.mediaRecord ?? null);
    if (!mediaId || interactionLocked || isUploadBlockedByCaseStatus) {
      return;
    }

    setInlineError('');
    remoteTranscriptInputRefs.current[mediaId]?.click();
  }, [getRecordMediaId, interactionLocked, isUploadBlockedByCaseStatus]);

  const handleRemoteTranscriptFileChange = useCallback(async (item, event) => {
    const file = event?.target?.files?.[0] ?? null;
    const mediaRecord = item?.mediaRecord ?? null;
    const mediaId = getRecordMediaId(mediaRecord);
    if (!mediaId) {
      showUploadBlockedDialog(UiMessages.playbackMissingIds);
      return;
    }

    if (!file) {
      return;
    }

    const validationMessage = getTranscriptFileValidationMessage(file);
    if (validationMessage) {
      showUploadBlockedDialog(validationMessage);
      if (event?.target) {
        event.target.value = '';
      }
      return;
    }

    const remoteItem = {
      mediaFile: null,
      transcriptFile: file,
      transcriptDescription: '',
      transcriptEnabled: true
    };

    const storedMediaFileName = getFirstRecordValue(mediaRecord, ['storedFileName', 'StoredFileName', 'fileName', 'FileName', 'originalFileName', 'OriginalFileName']) || '';

    try {
      setInlineError('');
      setRemoteTranscriptActionMediaId(mediaId);
      debugLog('remote-transcript-upload-start', {
        mediaId,
        name: file.name,
        size: file.size,
        type: file.type || ''
      });

      const transcriptUploadResult = await uploadRequests.uploadMultipartFile(file, {
        applicationNumber: config.applicationNumber,
        uploadedByUserId: config.userId || 'web-control',
        originalFileName: file.name,
        storedFileName: file.name,
        fileType: 'Transcript',
        mimeType: file.type || 'application/octet-stream',
        recordStatus: 'Draft',
        description: '',
        mediaId
      });

      const persistedTranscriptFileName = transcriptUploadResult?.completeData?.storedFileName
        || transcriptUploadResult?.completeData?.fileName
        || file.name;

      await uploadRequests.persistTranscriptRecord(remoteItem, mediaId, persistedTranscriptFileName, transcriptUploadResult?.completeData);

      debugLog('remote-transcript-upload-complete', {
        mediaId,
        storedMediaFileName,
        storedTranscriptFileName: persistedTranscriptFileName
      });

      await loadCaseFiles(false);
      if (hasPendingAnyScanWork(mediaItemsRef.current, caseFilesRef.current)) {
        queueCaseFilePollingRef.current?.();
      }
    } catch (error) { // NOSONAR
      showUploadBlockedDialog(GENERIC_UPLOAD_FAILURE_MESSAGE); 
    } finally {
      setRemoteTranscriptActionMediaId((current) => (current === mediaId ? '' : current));
      if (event?.target) {
        event.target.value = '';
      }
    }
  }, [caseFilesRef, config.applicationNumber, config.userId, debugLog, getFirstRecordValue, getRecordMediaId, getTranscriptFileValidationMessage, hasPendingAnyScanWork, loadCaseFiles, mediaItemsRef, setInlineError, uploadRequests]);

  function localItemMatchesRemoteCaseFile(localItem, remoteItem) {//NOSONAR
    return localUploadItemMatchesRemoteItem(localItem, remoteItem);
  }

  function removeLocalItemsForRemoteCaseFile(remoteItem) {
    setMediaItems((currentItems) => {
      const removedItemIds = currentItems
        .filter((item) => localItemMatchesRemoteCaseFile(item, remoteItem))
        .map((item) => item.id);

      if (removedItemIds.length === 0) {
        return currentItems;
      }

      removedItemIds.forEach((itemId) => {
        if (transcriptInputRefs.current[itemId]) {
          transcriptInputRefs.current[itemId].value = '';
          delete transcriptInputRefs.current[itemId];
        }
      });

      if (removedItemIds.length > 0 && mediaInputRef.current) {
        mediaInputRef.current.value = '';
      }

      const next = currentItems.filter((item) => !removedItemIds.includes(item.id));
      mediaItemsRef.current = next;
      return next;
    });
  }

  async function handleDeleteRemoteCaseFile(item) {
    const mediaRecord = item?.mediaRecord ?? null;
    const mediaId = mediaRecord?.mediaId || '';
    const mediaName = mediaRecord?.originalFileName || mediaRecord?.storedFileName || mediaId || 'media file';

    if (!mediaId || !managementApiService?.baseUrl || !config.caseId) {
      const message = 'Delete failed because mediaId, managementApiBaseUrl, or caseId is missing.';
      setRemoteDeleteError(message);
      setCaseFilesError(message);
      return false;
    }

    try {
      setCaseFilesError('');
      const response = await managementApiService.deleteMedia(config.caseId, mediaId, {
        userId: config.userId || 'web-control'
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error?.message || `delete media failed (${response.status})`);
      }

      setRemoteDeletePhase('refreshing');

      // Remove the deleted item from the current snapshot immediately. The
      // follow-up loadCaseFiles() will replace this with the authoritative API
      // result, but the optimistic update prevents stale virus/limit warnings
      // from hanging around while React waits for the next refresh/render.
      const optimisticallyFilteredCaseFiles = removeCaseFileItemFromRecords(caseFilesRef.current, item);
      caseFilesRef.current = optimisticallyFilteredCaseFiles;
      setCaseFiles(optimisticallyFilteredCaseFiles);
      removeLocalItemsForRemoteCaseFile(item);
      setInlineError((currentMessage) => clearResolvedInlineWarningIfNeeded(currentMessage, mediaItemsRef.current, optimisticallyFilteredCaseFiles));
      await waitForNextPaint();

      const refreshed = await loadCaseFiles(false);
      if (!refreshed) {
        throw new Error('delete succeeded, but the file list could not be refreshed');
      }

      removeLocalItemsForRemoteCaseFile(item);
      setInlineError((currentMessage) => clearResolvedInlineWarningIfNeeded(currentMessage, mediaItemsRef.current, caseFilesRef.current));
      await waitForNextPaint();
      return true;
    } catch (error) {
      const message = `Delete failed for ${mediaName}: ${error?.message || 'Unknown error'}`;
      setRemoteDeleteError(message);
      setCaseFilesError(message);
      return false;
    }
  }

  async function handleDeleteRemoteTranscriptFile(item) {
    const mediaRecord = item?.mediaRecord ?? null;
    const mediaId = mediaRecord?.mediaId || '';
    const transcriptName = getFirstRecordValue(getEffectiveTranscriptRecord(mediaRecord, item?.transcriptRecord ?? null), ['originalFileName', 'OriginalFileName', 'fileName', 'FileName', 'storedFileName', 'StoredFileName']) || 'transcript file';

    if (!mediaId || !managementApiService?.baseUrl || !config.caseId) {
      const message = 'Delete failed because mediaId, managementApiBaseUrl, or caseId is missing.';
      setRemoteDeleteError(message);
      setCaseFilesError(message);
      return false;
    }

    try {
      setCaseFilesError('');
      const response = await managementApiService.deleteTranscript(config.caseId, mediaId, {
        userId: config.userId || 'web-control'
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error?.message || `delete transcript failed (${response.status})`);
      }

      setRemoteDeletePhase('refreshing');
      const refreshed = await loadCaseFiles(false);
      if (!refreshed) {
        throw new Error('delete succeeded, but the file list could not be refreshed');
      }

      await waitForNextPaint();
      return true;
    } catch (error) {
      const message = `Delete failed for ${transcriptName}: ${error?.message || 'Unknown error'}`;
      setRemoteDeleteError(message);
      setCaseFilesError(message);
      return false;
    }
  }

  const {
    pendingRemoteDeleteItem,
    pendingRemoteTranscriptDeleteItem,
    remoteDeleteInProgress,
    remoteTranscriptDeleteInProgress,
    remoteDeletePhase,
    remoteDeleteError,
    setRemoteDeletePhase,
    setRemoteDeleteError,
    openRemoteDeleteDialog,
    openRemoteTranscriptDeleteDialog,
    closeRemoteDeleteDialog,
    closeRemoteTranscriptDeleteDialog,
    confirmDeleteRemoteCaseFile,
    confirmDeleteRemoteTranscriptFile
  } = useRemoteDeleteFlow({
    interactionLocked,
    isUploadBlockedByCaseStatus,
    waitForNextPaint,
    waitForMilliseconds,
    deleteDialogMinVisibleMs: DELETE_DIALOG_MIN_VISIBLE_MS,
    handleDeleteRemoteCaseFile,
    handleDeleteRemoteTranscriptFile
  });

  const {
    postJson, // NOSONAR
    uploadMultipartFile,
    persistMediaRecord,
    persistTranscriptRecord
  } = uploadRequests;

  async function markTimedOutRecordFailed(candidate) {
    if (!managementApiService?.baseUrl || !candidate?.caseId || !candidate?.storedFileName) {
      return false;
    }

    const response = await managementApiService.updateScanStatusByFile({
      caseId: candidate.caseId,
      mediaId: candidate.isTranscript ? (candidate.transcriptFileId || candidate.mediaId || '') : (candidate.mediaId || ''),
      isTranscript: Boolean(candidate.isTranscript),
      fileName: candidate.storedFileName,
      scanStatus: 'failed'
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.error?.message || `update-scan-status-by-file failed (${response.status})`);
    }

    return true;
  }

  async function processTimedOutRemoteScanCandidates(candidates) {
    const normalizedCandidates = Array.isArray(candidates) ? candidates : [];
    if (normalizedCandidates.length === 0) {
      return false;
    }

    let hasUpdates = false;
    for (const candidate of normalizedCandidates) {
      const requestKey = buildTimedOutCandidateRequestKey(candidate);
      if (!requestKey || processingTimeoutRequestKeysRef.current.has(requestKey)) {
        continue;
      }

      processingTimeoutRequestKeysRef.current.add(requestKey);
      try {
        const updated = await markTimedOutRecordFailed(candidate);
        hasUpdates = hasUpdates || updated;
        debugLog('processing-timeout-failed-request-completed', {
          requestKey,
          updated,
          candidate
        });
      } catch (error) {
        debugLog('processing-timeout-failed-request-error', {
          requestKey,
          candidate,
          message: error?.message || 'Unknown error'
        });
        processingTimeoutRequestKeysRef.current.delete(requestKey);
      }
    }

    return hasUpdates;
  }

  const queueCaseFilePolling = useCallback(() => {
    const pollingSnapshot = {
      hasBaseUrl: Boolean(managementApiService?.baseUrl),
      caseId: config.caseId || '',
      active: caseFilePollingActiveRef.current,
      stopped: caseFilePollingStoppedRef.current,
      inFlight: caseFilePollingInFlightRef.current,
      hasTimer: caseFilePollingTimerRef.current !== null,
      hasPendingAny: hasPendingAnyScanWork(mediaItemsRef.current, caseFilesRef.current),
      localPendingCount: mediaItemsRef.current.filter((item) => hasPendingScanWork([item])).length,
      remoteCount: Array.isArray(caseFilesRef.current) ? caseFilesRef.current.length : 0
    };

    debugLog('queue-case-file-polling-called', pollingSnapshot);

    if (!managementApiService?.baseUrl || !config.caseId || caseFilePollingActiveRef.current) {
      debugLog('queue-case-file-polling-skipped', pollingSnapshot);
      return;
    }

    caseFilePollingStoppedRef.current = false;
    caseFilePollingActiveRef.current = true;

    if (caseFilePollingTimerRef.current !== null || caseFilePollingInFlightRef.current) {
      debugLog('queue-case-file-polling-already-running', pollingSnapshot);
      return;
    }

    const poll = async () => {
      caseFilePollingTimerRef.current = null;

      const beforePollSnapshot = {
        stopped: caseFilePollingStoppedRef.current,
        active: caseFilePollingActiveRef.current,
        inFlight: caseFilePollingInFlightRef.current,
        hasPendingAny: hasPendingAnyScanWork(mediaItemsRef.current, caseFilesRef.current),
        localItemsCount: mediaItemsRef.current.length,
        remoteCount: Array.isArray(caseFilesRef.current) ? caseFilesRef.current.length : 0
      };

      debugLog('case-file-poll-tick', beforePollSnapshot);

      if (caseFilePollingStoppedRef.current || !hasPendingAnyScanWork(mediaItemsRef.current, caseFilesRef.current)) {
        caseFilePollingActiveRef.current = false;
        debugLog('case-file-poll-stopped-before-fetch', beforePollSnapshot);
        return;
      }

      if (caseFilePollingInFlightRef.current) {
        debugLog('case-file-poll-skip-in-flight', beforePollSnapshot);
        return;
      }

      caseFilePollingInFlightRef.current = true;
      try {
        await loadCaseFiles(false);
      } finally {
        caseFilePollingInFlightRef.current = false;
      }

      const afterPollSnapshot = {
        stopped: caseFilePollingStoppedRef.current,
        active: caseFilePollingActiveRef.current,
        inFlight: caseFilePollingInFlightRef.current,
        hasPendingAny: hasPendingAnyScanWork(mediaItemsRef.current, caseFilesRef.current),
        localItemsCount: mediaItemsRef.current.length,
        remoteCount: Array.isArray(caseFilesRef.current) ? caseFilesRef.current.length : 0
      };

      debugLog('case-file-poll-fetched', afterPollSnapshot);

      if (caseFilePollingStoppedRef.current || !hasPendingAnyScanWork(mediaItemsRef.current, caseFilesRef.current)) {
        caseFilePollingActiveRef.current = false;
        debugLog('case-file-poll-finished', afterPollSnapshot);
        return;
      }

      caseFilePollingTimerRef.current = globalThis.setTimeout(poll, 10000);
      debugLog('case-file-poll-rescheduled', {
        delayMs: 10000,
        ...afterPollSnapshot
      });
    };

    caseFilePollingTimerRef.current = globalThis.setTimeout(poll, 10000);
    debugLog('queue-case-file-polling-started', {
      delayMs: 10000,
      caseId: config.caseId,
      baseUrl: managementApiService?.baseUrl
    });
  }, [config.caseId, debugLog, hasPendingAnyScanWork, hasPendingScanWork, loadCaseFiles, managementApiService?.baseUrl]);

  queueCaseFilePollingRef.current = queueCaseFilePolling;

  useEffect(() => {
    const effectSnapshot = {
      hasPendingScanWorkNow,
      hasBaseUrl: Boolean(managementApiService?.baseUrl),
      caseId: config.caseId || '',
      active: caseFilePollingActiveRef.current,
      stopped: caseFilePollingStoppedRef.current,
      inFlight: caseFilePollingInFlightRef.current,
      hasTimer: caseFilePollingTimerRef.current !== null
    };

    debugLog('case-file-polling-effect', effectSnapshot);

    if (!hasPendingScanWorkNow || !managementApiService?.baseUrl || !config.caseId) {
      stopCaseFilePolling();
      return undefined;
    }

    queueCaseFilePolling();

    return () => stopCaseFilePolling();
  }, [config.caseId, debugLog, hasPendingScanWorkNow, managementApiService?.baseUrl, queueCaseFilePolling, stopCaseFilePolling]);

    async function pollStatusesUntilReady() { // NOSONAR
    const maxAttempts = 120;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      if (!hasPendingScanWork(mediaItemsRef.current)) {
        return {
          hasVirus: mediaItemsRef.current.some((item) => isVirusStatus(item.scanStatus) || isVirusStatus(item.transcriptScanStatus)),
          nextItems: mediaItemsRef.current
        };
      }

      // Fetch the full case file list here as well. Transcript terminal scan
      // statuses can be returned on the full list or embedded under the media
      // record instead of by the keyed polling response.
      const remoteFiles = await fetchCaseFiles();
      const currentItems = mediaItemsRef.current;
      const nextItems = mergeRemoteStatuses(currentItems, remoteFiles);

      const dedupedRemoteFiles = dedupeCaseFileRecords(remoteFiles);
      mediaItemsRef.current = nextItems;
      caseFilesRef.current = dedupedRemoteFiles;
      setMediaItems(nextItems);
      setCaseFiles(dedupedRemoteFiles);

      const hasPendingMedia = nextItems.some((item) => !isTerminalScanStatus(item.scanStatus));
      const hasPendingTranscript = nextItems.some((item) => item.transcriptEnabled && !isTerminalScanStatus(item.transcriptScanStatus));
      const hasPendingRemote = hasPendingRemoteScanWork(dedupedRemoteFiles);
      const hasVirus = nextItems.some((item) => isVirusStatus(item.scanStatus) || isVirusStatus(item.transcriptScanStatus));

      if (!hasPendingMedia && !hasPendingTranscript && !hasPendingRemote) {
        return {
          hasVirus,
          nextItems
        };
      }

      await new Promise((resolve) => setTimeout(resolve, 10000));
    }

    throw new Error(UiMessages.scanTimeout);
  }

  const { handleUploadClick } = useUploadWorkflow({
    config,
    caseFilesRef,
    mediaItems,
    mediaItemsRef,
    pendingUploadItems,
    interactionLocked,
    inlineError,
    caseUploadBlockedMessage,
    isUploadBlockedByCaseStatus,
    loadCaseFiles,
    queueCaseFilePolling,
    shouldCountLocalUploadItem,
    getUploadLimitValidationMessage,
    hasPendingAnyScanWork,
    buildProcessingDeadlineBatchItem,
    collectDeadlineMatchedRemoteScanCandidates,
    processTimedOutRemoteScanCandidates,
    isTerminalScanStatus,
    uploadMultipartFile,
    persistMediaRecord,
    persistTranscriptRecord,
    updateItemStatus,
    updateMediaItem,
    setMediaItems,
    setInlineError,
    setCompletionDialog,
    setIsUiLocked,
    debugLog,
    uiMessages: UiMessages,
    processingMaxTimeSeconds: PROCESSING_MAX_TIME_SECONDS,
    withReason,
    showUploadBlockedDialog
  });

  useEffect(() => {
    const isModalOpen = Boolean(completionDialog) || pendingDeleteItemId !== null || pendingTranscriptDeleteItemId !== null || pendingRemoteDeleteItem !== null || pendingRemoteTranscriptDeleteItem !== null;
    if (!isModalOpen) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onEscape(event) {
      if (event.key !== 'Escape') {
        return;
      }

      if (localDeleteInProgress || transcriptDeleteInProgress || remoteDeleteInProgress || remoteTranscriptDeleteInProgress) {
        return;
      }

      setCompletionDialog(null);
      setPendingDeleteItemId(null);
      setPendingTranscriptDeleteItemId(null);
      closeRemoteDeleteDialog();
      closeRemoteTranscriptDeleteDialog();
    }

    globalThis.addEventListener('keydown', onEscape);

    return () => {
      document.body.style.overflow = originalOverflow;
      globalThis.removeEventListener('keydown', onEscape);
    };
  }, [closeRemoteDeleteDialog, closeRemoteTranscriptDeleteDialog, completionDialog, localDeleteInProgress, pendingDeleteItemId, pendingRemoteDeleteItem, pendingRemoteTranscriptDeleteItem, pendingTranscriptDeleteItemId, remoteDeleteInProgress, remoteTranscriptDeleteInProgress, transcriptDeleteInProgress]);

  const handleSaveRemoteDescription = useCallback(async ({ record, description, isTranscript = false }) => {
    const normalizedDescription = normalizeEditableDescription(description);
    const caseId = String(getFirstRecordValue(record, ['caseId', 'CaseId']) || config.caseId || '').trim();
    const mediaId = String(getRecordMediaId(record) || '').trim();
    const applicationId = String(
      getFirstRecordValue(record, ['clientApplicationId', 'ClientApplicationId', 'applicationId', 'ApplicationId'])
      || remoteStatusSummaryRef.current?.applicationId
      || remoteStatusSummaryRef.current?.ApplicationId
      || ''
    ).trim();
    const applicationNumber = String(getFirstRecordValue(record, ['applicationNumber', 'ApplicationNumber']) || config.applicationNumber || '').trim();

    if (!managementApiService?.baseUrl) {
      throw new Error('Description update is unavailable right now.');
    }

    if (!applicationId || !caseId || !mediaId) {
      throw new Error('Missing file identity for description update.');
    }

    const response = await managementApiService.updateDescription({
      applicationId,
      applicationNumber,
      caseId,
      mediaId,
      description: normalizedDescription,
      isTranscript: Boolean(isTranscript)
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.error?.message || `Description update failed (${response.status})`);
    }

    patchRemoteDescriptionState({
      mediaId,
      isTranscript: Boolean(isTranscript),
      description: normalizedDescription
    });

    loadCaseFiles(false).catch(() => false);

    return {
      description: normalizedDescription,
      payload
    };
  }, [config.applicationNumber, config.caseId, getFirstRecordValue, getRecordMediaId, loadCaseFiles, managementApiService, patchRemoteDescriptionState, remoteStatusSummaryRef]);

  const virusBannerMessage = showVirusBanner ? VIRUS_BLOCK_MESSAGE : '';

  return (
    <div className={`upload-widget-shell ${isUiLocked ? 'upload-widget-shell-locked' : ''} ${showVirusBanner ? 'upload-widget-shell-virus' : ''}`} data-case-id={config.caseId}>
      <div className="upload-widget-header">
        <div>
          <h1>{UiMessages.pageTitle}</h1>
          {!isReadOnly ? <p>{getPageIntroMessage()}</p> : null} {/* NOSONAR */}
          {!isReadOnly ? <p className="transcript-recommend-text">{UiMessages.transcriptOptional}</p> : null} {/* NOSONAR */}
          {!isReadOnly ? ( // NOSONAR
            <div className="supported-types-text">
              {getSupportedTypesMessage()}
            </div>
          ) : null}
        </div>
      </div>

      {validationMessage ? <div className="validation-banner">{validationMessage}</div> : null}
      {virusBannerMessage ? <div className="virus-banner">{virusBannerMessage}</div> : null}
      {isUiLocked ? <div className="upload-lock-banner">{UiMessages.uiLocked}</div> : null}

      <div className="case-files-shell">
        {caseFilesLoading ? <div className="case-files-loading">{UiMessages.refreshing}</div> : null}
        {caseFilesError ? <div className="case-files-error">{caseFilesError}</div> : null}

        <CaseFileGroup
          title="All files"
          items={isReadOnly ? visibleCaseFileItems : buildCaseFileItems(caseFiles)}
          isReadOnly={interactionLocked || isUploadBlockedByCaseStatus}
          canOpenPlayer={!isCaseBlockedByStatus && isReadOnly}
          onDelete={openRemoteDeleteDialog}
          onDeleteTranscript={openRemoteTranscriptDeleteDialog}
          onOpenPlayer={openPlayerPopup}
          onBrowseAddTranscript={handleBrowseAddTranscript}
          onRemoteTranscriptFileChange={handleRemoteTranscriptFileChange}
          setRemoteTranscriptInputRef={setRemoteTranscriptInputRef}
          transcriptAccept={TRANSCRIPT_ACCEPT}
          isAddingTranscript={(item) => remoteTranscriptActionMediaId === (getRecordMediaId(item?.mediaRecord ?? null) || '')}
          cardProps={{
            getEffectiveTranscriptRecord,
            resolveCaseFileMediaTone,
            resolveCaseFileTranscriptTone,
            isVirusDetectedMediaRecord,
            isTranscriptVirusDetected,
            isVirusStatus,
            isLocalUploadFailed,
            getFirstRecordValue,
            getRecordMediaId,
            getRecordTranscriptFileId,
            onSaveDescription: handleSaveRemoteDescription
          }}
        />
      </div>

      {canRenderUploadControls ? (
        <>
          {/* NOSONAR */}<div
            className={`drop-panel ${isReadOnly ? 'drop-panel-readonly' : ''}`}
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleDrop}
          >
            <input
              ref={mediaInputRef}
              type="file"
              className="d-none"
              accept={MEDIA_ACCEPT}
              multiple
              disabled={interactionLocked}
              onChange={handleMediaFileChange}
            />

            <button
              type="button"
              className="browse-button"
              disabled={interactionLocked}
              onClick={() => mediaInputRef.current?.click()}
            >
              {UiMessages.chooseFiles}
            </button>

            <span className="drop-hint">{UiMessages.dropHint}</span>
          </div>

          <UploadListSection
            items={visibleUploadItems}
            panelStyle={failedUploadQueuePanelStyle}
            interactionLocked={interactionLocked}
            isUploadBlockedByCaseStatus={isUploadBlockedByCaseStatus}
            transcriptAccept={TRANSCRIPT_ACCEPT}
            getMediaSizeText={(item) => formatSize(item.mediaFile.size)}
            getPresentation={(item) => getUploadRowPresentation(item, isLocalUploadFailed)}
            onDescriptionChange={handleDescriptionChange}
            onMediaDelete={setPendingDeleteItemId}
            onTranscriptToggle={handleTranscriptToggle}
            onTranscriptFileChange={handleTranscriptFileChange}
            onBrowseTranscript={(item) => transcriptInputRefs.current[item.id]?.click()}
            onTranscriptDescriptionChange={handleTranscriptDescriptionChange}
            onTranscriptDelete={(currentItem) => {
              if (currentItem.transcriptFile) {
                setPendingTranscriptDeleteItemId(currentItem.id);
                return;
              }

              handleRemoveTranscriptFile(currentItem.id);
            }}
            setTranscriptInputRef={setTranscriptInputRef}
            renderDebug={(items) => debugLog('render-visible-items', {
              count: items.length,
              names: items.map((item) => item.mediaFile?.name).filter(Boolean)
            }) || null}
          />
        </>
      ) : null}

      {canRenderUploadControls ? (
        <div className="upload-submit-row">
          <button
            type="button"
            className="btn btn-secondary upload-submit-button"
            disabled={interactionLocked}
            onClick={handleUploadClick}
          >
            Upload
          </button>
        </div>
      ) : null}

      {isUiLocked ? <UploadProcessingOverlay /> : null}

      {completionDialog ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true"> {/* NOSONAR */}
            <div className="modal-dialog modal-dialog-centered upload-result-modal-dialog" role="document"> {/* NOSONAR */}
              <div className={`modal-content upload-result-modal upload-result-modal-${completionDialog.tone}`}>
                <div className="modal-header">
                  <h5 className="modal-title">{completionDialog.title}</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setCompletionDialog(null)} />
                </div>
                <div className="modal-body">
                  {completionDialog.selectionSummary ? (
                    <div className="upload-selection-feedback-shell upload-selection-feedback-shell-modal">
                      {completionDialog.selectionSummary.accepted.length > 0 ? (
                        <div className="upload-selection-panel upload-selection-panel-success">
                          <div className="upload-selection-panel-header">
                            <div className="upload-selection-panel-title">Selected successfully</div>
                            <div className="upload-selection-panel-count">{completionDialog.selectionSummary.accepted.length} file(s)</div>
                          </div>
                          <ul className="upload-selection-panel-list mb-0">
                            {completionDialog.selectionSummary.accepted.map((item) => (
                              <li key={item.id}>
                                <div className="upload-selection-panel-file-line">
                                  <strong className="upload-selection-panel-file-name">{item.fileName}</strong>
                                  <span className="upload-selection-panel-file-meta">{formatSize(item.size)}</span>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}

                      {completionDialog.selectionSummary.rejected.length > 0 ? (
                        <div className="upload-selection-panel upload-selection-panel-error">
                          <div className="upload-selection-panel-header">
                            <div className="upload-selection-panel-title">Files not added</div>
                            <div className="upload-selection-panel-count">{completionDialog.selectionSummary.rejected.length} file(s)</div>
                          </div>
                          <ul className="upload-selection-panel-list mb-0">
                            {completionDialog.selectionSummary.rejected.map((item) => (
                              <li key={item.id}>
                                <div className="upload-selection-panel-file-line">
                                  <strong className="upload-selection-panel-file-name">{item.fileName}</strong>
                                  <span className="upload-selection-panel-file-meta">{formatSize(item.size)}</span>
                                </div>
                                <div className="upload-selection-panel-reason">{item.reason}</div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <p className="mb-0">{completionDialog.message}</p>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-primary" onClick={() => setCompletionDialog(null)}>
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => setCompletionDialog(null)} /> {/* NOSONAR */}
        </>
      ) : null}

      {pendingDeleteItemId ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true"> {/* NOSONAR */}
            <div className="modal-dialog modal-dialog-centered" role="document"> {/* NOSONAR */}
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{UiMessages.confirmDeleteFileTitle}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    disabled={localDeleteInProgress}
                    onClick={() => setPendingDeleteItemId(null)}
                  />
                </div>
                <div className="modal-body">
                  <p className="mb-0">{UiMessages.confirmDeleteFileText}</p>
                  {localDeleteInProgress ? (
                    <div className="delete-progress-row" role="status" aria-live="polite"> {/* NOSONAR */}
                      <span className="status-spinner delete-dialog-spinner" aria-hidden="true" />
                      <span>{UiMessages.deleteInProgress}</span>
                    </div>
                  ) : null}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" disabled={localDeleteInProgress} onClick={() => setPendingDeleteItemId(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={localDeleteInProgress}
                    onClick={() => confirmRemoveMediaFile()}
                  >
                    {localDeleteInProgress ? (
                      <>
                        <span className="btn-inline-spinner" aria-hidden="true" />
                        {' '}Deleting...
                      </>
                    ) : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={localDeleteInProgress ? undefined : () => setPendingDeleteItemId(null)} /> {/* NOSONAR */}
        </>
      ) : null}

      {pendingRemoteDeleteItem ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true"> {/* NOSONAR */}
            <div className="modal-dialog modal-dialog-centered" role="document"> {/* NOSONAR */}
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{UiMessages.confirmDeleteCaseFileTitle}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    disabled={remoteDeleteInProgress}
                    onClick={closeRemoteDeleteDialog}
                  />
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    Delete <b>{pendingRemoteDeleteItem?.mediaRecord?.originalFileName || pendingRemoteDeleteItem?.mediaRecord?.storedFileName || 'this file'}</b> from the case?
                  </p>
                  {remoteDeleteInProgress ? (
                    <div className="delete-progress-row" role="status" aria-live="polite"> {/* NOSONAR */}
                      <span className="status-spinner delete-dialog-spinner" aria-hidden="true" />
                      <span>{remoteDeletePhase === 'refreshing' ? 'Refreshing the file list. Please wait...' : 'Deleting file. Please wait...'}</span>
                    </div>
                  ) : null}
                  {remoteDeleteError ? <div className="delete-dialog-error">{remoteDeleteError}</div> : null}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" disabled={remoteDeleteInProgress} onClick={closeRemoteDeleteDialog}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={remoteDeleteInProgress}
                    onClick={() => confirmDeleteRemoteCaseFile()
                    }
                  >
                    {remoteDeleteInProgress ? (
                      <>
                        <span className="btn-inline-spinner" aria-hidden="true" />
                        {' '}Deleting...
                      </>
                    ) : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={remoteDeleteInProgress ? undefined : closeRemoteDeleteDialog} /> {/* NOSONAR */}
        </>
      ) : null}

      {pendingRemoteTranscriptDeleteItem ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true"> {/* NOSONAR */}
            <div className="modal-dialog modal-dialog-centered" role="document"> {/* NOSONAR */}
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{UiMessages.deleteRemoteTranscriptTitle}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    disabled={remoteTranscriptDeleteInProgress}
                    onClick={closeRemoteTranscriptDeleteDialog}
                  />
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    Delete <b>{pendingRemoteTranscriptDeleteItem?.transcriptRecord?.originalFileName || pendingRemoteTranscriptDeleteItem?.transcriptRecord?.storedFileName || 'this transcript'}</b> and keep the media file?
                  </p>
                  {remoteTranscriptDeleteInProgress ? (
                    <div className="delete-progress-row" role="status" aria-live="polite"> {/* NOSONAR */}
                      <span className="status-spinner delete-dialog-spinner" aria-hidden="true" />
                      <span>{remoteDeletePhase === 'refreshing' ? UiMessages.deleteRefreshing : UiMessages.deleteTranscriptInProgress}</span>
                    </div>
                  ) : null}
                  {remoteDeleteError ? <div className="delete-dialog-error">{remoteDeleteError}</div> : null}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" disabled={remoteTranscriptDeleteInProgress} onClick={closeRemoteTranscriptDeleteDialog}>
                    {UiMessages.buttonCancel}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={remoteTranscriptDeleteInProgress}
                    onClick={() => confirmDeleteRemoteTranscriptFile()
                    }
                  >
                    {remoteTranscriptDeleteInProgress ? (
                      <>
                        <span className="btn-inline-spinner" aria-hidden="true" />
                        {' '}{UiMessages.deleting}
                      </>
                    ) : UiMessages.buttonDelete}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={remoteTranscriptDeleteInProgress ? undefined : closeRemoteTranscriptDeleteDialog} /> {/* NOSONAR */}
        </>
      ) : null}

      {pendingTranscriptDeleteItemId ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true"> {/* NOSONAR */}
            <div className="modal-dialog modal-dialog-centered" role="document"> {/* NOSONAR */}
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{UiMessages.confirmDeleteTranscriptTitle}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    disabled={transcriptDeleteInProgress}
                    onClick={() => setPendingTranscriptDeleteItemId(null)}
                  />
                </div>
                <div className="modal-body">
                  <p className="mb-0">{UiMessages.confirmDeleteTranscriptText}</p>
                  {transcriptDeleteInProgress ? (
                    <div className="delete-progress-row" role="status" aria-live="polite"> {/* NOSONAR */}
                      <span className="status-spinner delete-dialog-spinner" aria-hidden="true" />
                      <span>{UiMessages.deleteTranscriptInProgress}</span>
                    </div>
                  ) : null}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" disabled={transcriptDeleteInProgress} onClick={() => setPendingTranscriptDeleteItemId(null)}>
                    {UiMessages.buttonCancel}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={transcriptDeleteInProgress}
                    onClick={() => confirmRemoveTranscriptFile()}
                  >
                    {transcriptDeleteInProgress ? (
                      <>
                        <span className="btn-inline-spinner" aria-hidden="true" />
                        {' '}{UiMessages.deleting}
                      </>
                    ) : UiMessages.buttonDelete}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={transcriptDeleteInProgress ? undefined : () => setPendingTranscriptDeleteItemId(null)} /> {/* NOSONAR */}
        </>
      ) : null}
    </div>
  );
}

UploadWidget.propTypes = {
  config: PropTypes.object
};

let uploadWebControlRoot = null;
let uploadWebControlContainer = null;

function unmountUploadWebControl() {
  if (uploadWebControlRoot !== null) {
    uploadWebControlRoot.unmount();
    uploadWebControlRoot = null;
  }

  uploadWebControlContainer = null;
}

function renderUploadWebControl() {
  const rootElement = document.getElementById('upload-control-root');
  if (rootElement === null) {
    unmountUploadWebControl();
    return;
  }

  if (uploadWebControlContainer !== null && uploadWebControlContainer !== rootElement) {
    unmountUploadWebControl();
  }

  const config = loadConfig();
  if (uploadWebControlRoot === null) {
    uploadWebControlContainer = rootElement;
    uploadWebControlRoot = ReactDOM.createRoot(rootElement);
  }

  uploadWebControlRoot.render(
    <UploadWidget config={config} />
  );
}

globalThis.StateCourtsMusUploadWebControl = globalThis.StateCourtsMusUploadWebControl || {};
globalThis.StateCourtsMusUploadWebControl.mount = function mount(config) {
  globalThis.__UPLOAD_WEB_CONTROL_CONFIG__ = normalizeUploadWebControlConfig(config || {});
  if (uploadWebControlContainer !== document.getElementById('upload-control-root')) {
    unmountUploadWebControl();
  }
  renderUploadWebControl();
};
globalThis.StateCourtsMusUploadWebControl.GetStatusSummary = async function GetStatusSummary() {
  const config = loadConfig();
  const localStatus = globalThis.__UPLOAD_WEB_CONTROL_STATUS__ ?? null;

  let remoteStatusSummary = localStatus?.remoteStatusSummary ?? null;
  try {
    const managementApiService = createManagementApiService(config);
    const fetchedSummary = await fetchApplicationFileStatusSummary(managementApiService, config);
    if (fetchedSummary) {
      remoteStatusSummary = fetchedSummary;
    }
  } catch (error) {
    return {
      ...(localStatus ?? {}), // NOSONAR
      remoteStatusSummary,
      summaryFetchError: error?.message || 'Unknown error'
    };
  }

  if (localStatus) {
    const rebuiltStatus = buildStatusSummaryDetail({
      config,
      acknowledged: localStatus.acknowledged ?? true,
      validationMessage: localStatus.validationMessage ?? '',
      hasVirusDetected: localStatus.hasVirusDetected ?? false,
      completionSummary: {
        overallStatus: localStatus.overallStatus ?? 'idle',
        readyToSubmit: localStatus.readyToSubmit ?? false,
        canUpload: localStatus.canUpload ?? false,
        completedCount: localStatus.completedCount ?? 0,
        errorCount: localStatus.localUploadSummary?.uploadFailedFileCount ?? 0,
        pendingCount: localStatus.pendingCount ?? 0,
        itemSummaries: Array.isArray(localStatus.fileStatuses) ? localStatus.fileStatuses : []
      },
      mediaItems: Array.isArray(localStatus.fileStatuses) ? localStatus.fileStatuses : [],
      totalSelectedBytes: localStatus.totalSelectedBytes ?? 0,
      localUploadingCount: localStatus.localUploadSummary?.uploadingCount ?? 0,
      localSelectedNotUploadedCount: localStatus.localUploadSummary?.selectedNotUploadedCount ?? 0,
      pendingUploadCount: localStatus.localUploadSummary?.pendingUploadCount ?? 0,
      remoteStatusSummary,
    });

    return {
      ...rebuiltStatus,
      summaryFetchError: localStatus.summaryFetchError
    };
  }

  return buildStatusSummaryDetail({
    config,
    acknowledged: true,
    validationMessage: '',
    hasVirusDetected: false,
    completionSummary: {
      overallStatus: 'idle',
      readyToSubmit: false,
      canUpload: false,
      completedCount: 0,
      errorCount: 0,
      pendingCount: 0,
      itemSummaries: []
    },
    mediaItems: [],
    totalSelectedBytes: 0,
    localUploadingCount: 0,
    localSelectedNotUploadedCount: 0,
    pendingUploadCount: 0,
    remoteStatusSummary
  });
};
globalThis.StateCourtsMusUploadWebControl.reinit = function reinit(config) {
  if (typeof config !== 'undefined') { // NOSONAR
    globalThis.__UPLOAD_WEB_CONTROL_CONFIG__ = normalizeUploadWebControlConfig(config || {});
  }

  if (uploadWebControlContainer !== document.getElementById('upload-control-root')) {
    unmountUploadWebControl();
  }

  renderUploadWebControl();
};
globalThis.StateCourtsMusUploadWebControl.unmount = function unmount() {
  unmountUploadWebControl();
};

renderUploadWebControl();
